/**
 * AdminBooking — Team booking configurator + upcoming bookings.
 * Team roster (existing users) + per-user booking profile config.
 * Compact single-page: pick a user → edit meeting types + hours → save.
 */
import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Calendar, Plus, Copy, Trash, Sparkle, User, Clock, House, Link as LinkIcon,
  Lightning, Users, PaperPlaneTilt,
} from "@phosphor-icons/react";

const DEFAULT_HOURS = { "0":[{start:"09:00",end:"17:00"}], "1":[{start:"09:00",end:"17:00"}],
  "2":[{start:"09:00",end:"17:00"}], "3":[{start:"09:00",end:"17:00"}], "4":[{start:"09:00",end:"17:00"}] };
const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const DEFAULT_TYPES = [
  { slug:"site-walk", name:"Vendor Site Walk", duration_min:60, buffer_min:15, color:"#0891b2", description:"Meet vendor at a property." },
  { slug:"walkthrough", name:"Landlord Walkthrough", duration_min:45, buffer_min:15, color:"#0f766e", description:"Meet with a landlord/owner." },
  { slug:"quick-chat", name:"Quick Chat", duration_min:15, buffer_min:5, color:"#64748b", description:"Short phone/in-person chat." },
];

export default function AdminBooking() {
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [current, setCurrent] = useState(null);   // active profile draft
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [u, p, b] = await Promise.all([
        api.get("/users"),
        api.get("/booking/profiles"),
        api.get("/booking/bookings"),
      ]);
      setUsers((u.data || []).filter(x => ["admin", "worker"].includes(x.role)));
      setProfiles(p.data.items || []);
      setBookings(b.data.items || []);
    } catch (e) { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (user) => {
    const existing = profiles.find(p => p.user_id === user.id);
    setCurrent(existing ? { ...existing } : {
      user_id: user.id, display_name: user.name || user.email, title: user.role,
      tz: "America/New_York", working_hours: { ...DEFAULT_HOURS }, blackout_dates: [],
      meeting_types: [...DEFAULT_TYPES], active: true,
    });
  };
  const save = async () => {
    if (!current) return;
    setBusy(true);
    try {
      const { data } = await api.post("/booking/profiles", current);
      toast.success("Saved");
      setCurrent(data);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };
  const copyLink = async (slug) => {
    const url = `${window.location.origin}/book/${slug}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Copy failed"); }
  };
  const cancelBooking = async (bid) => {
    if (!window.confirm("Cancel this booking?")) return;
    try { await api.post(`/booking/bookings/${bid}/cancel`); toast.success("Canceled"); await load(); }
    catch { toast.error("Failed"); }
  };

  const updField = (k) => (v) => setCurrent(c => ({ ...c, [k]: v }));
  const updHours = (d, i, k, v) => setCurrent(c => ({ ...c, working_hours: { ...c.working_hours, [d]: c.working_hours[d].map((r, idx) => idx === i ? { ...r, [k]: v } : r) } }));
  const addHour  = (d) => setCurrent(c => ({ ...c, working_hours: { ...c.working_hours, [d]: [...(c.working_hours[d] || []), { start:"09:00", end:"17:00" }] } }));
  const rmHour   = (d, i) => setCurrent(c => ({ ...c, working_hours: { ...c.working_hours, [d]: (c.working_hours[d] || []).filter((_, idx) => idx !== i) } }));
  const updMT    = (i, k, v) => setCurrent(c => ({ ...c, meeting_types: c.meeting_types.map((m, idx) => idx === i ? { ...m, [k]: k === "duration_min" || k === "buffer_min" ? parseInt(v)||0 : v } : m) }));
  const addMT    = () => setCurrent(c => ({ ...c, meeting_types: [...c.meeting_types, { slug:`type-${c.meeting_types.length+1}`, name:"New Meeting", duration_min:30, buffer_min:0, color:"#0f172a", description:"" }] }));
  const rmMT     = (i) => setCurrent(c => ({ ...c, meeting_types: c.meeting_types.filter((_, idx) => idx !== i) }));

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto p-4 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-slate-800 to-slate-950 text-white">
            <Calendar weight="fill" size={26}/>
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900">Booking Radar</h1>
            <p className="text-sm text-slate-500">Each teammate has their own public booking link. Vendors & landlords self-serve.</p>
          </div>
        </div>

        {/* Team roster */}
        <div className="grid gap-2 sm:grid-cols-2">
          {users.map(u => {
            const prof = profiles.find(p => p.user_id === u.id);
            const url = prof ? `${window.location.origin}/book/${prof.slug}` : null;
            return (
              <div key={u.id} className="rounded-xl bg-white border-2 border-slate-200 p-3">
                <div className="flex items-center gap-2">
                  <User weight="fill" size={18} className="text-slate-500"/>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-slate-900">{u.name || u.email}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest">{u.role}</div>
                  </div>
                  <button onClick={() => startEdit(u)}
                    className="h-8 px-2 bg-slate-900 hover:bg-black text-white text-[10px] font-black uppercase tracking-widest"
                    data-testid={`booking-edit-${u.id}`}>
                    {prof ? "Edit" : "Enable"}
                  </button>
                </div>
                {prof && url && (
                  <div className="mt-2 flex items-center gap-1">
                    <div className="flex-1 text-[10px] font-mono text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1 truncate">{url}</div>
                    <button onClick={() => copyLink(prof.slug)} className="h-7 w-7 bg-slate-900 text-white flex items-center justify-center"
                      data-testid={`booking-copy-${u.id}`}>
                      <Copy weight="bold" size={11}/>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Editor modal-ish */}
        {current && (
          <div className="fixed inset-0 bg-slate-900/70 z-40 flex items-start justify-center p-2 sm:p-4">
            <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
              <div className="p-3 bg-slate-900 text-white flex items-start justify-between sticky top-0 z-10">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest opacity-70">Booking Profile</div>
                  <div className="font-black text-lg">{current.display_name}</div>
                </div>
                <button onClick={() => setCurrent(null)} className="p-1 hover:bg-black/30 text-xl">✕</button>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Display Name</label>
                    <input value={current.display_name} onChange={e => updField("display_name")(e.target.value)}
                      className="mt-1 w-full h-9 px-2 border-2 border-slate-300 text-sm"/>
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Title (optional)</label>
                    <input value={current.title || ""} onChange={e => updField("title")(e.target.value)}
                      className="mt-1 w-full h-9 px-2 border-2 border-slate-300 text-sm"/>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Meeting Types</label>
                    <button onClick={addMT} className="text-[10px] font-bold text-slate-800 hover:text-black flex items-center gap-0.5">
                      <Plus weight="bold" size={11}/> Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {current.meeting_types.map((m, i) => (
                      <div key={i} className="rounded-md border-2 border-slate-200 bg-slate-50 p-2">
                        <div className="grid grid-cols-12 gap-1">
                          <input value={m.name} onChange={e => updMT(i, "name", e.target.value)} placeholder="Name"
                            className="col-span-5 h-9 px-2 border border-slate-200 text-sm bg-white"/>
                          <input type="number" value={m.duration_min} onChange={e => updMT(i, "duration_min", e.target.value)}
                            className="col-span-2 h-9 px-2 border border-slate-200 text-sm bg-white text-right" title="Duration (min)"/>
                          <input type="number" value={m.buffer_min || 0} onChange={e => updMT(i, "buffer_min", e.target.value)}
                            className="col-span-2 h-9 px-2 border border-slate-200 text-sm bg-white text-right" title="Buffer (min)"/>
                          <input value={m.slug} onChange={e => updMT(i, "slug", e.target.value)} placeholder="slug"
                            className="col-span-2 h-9 px-2 border border-slate-200 text-xs bg-white font-mono"/>
                          <button onClick={() => rmMT(i)} className="col-span-1 text-slate-400 hover:text-red-500 flex items-center justify-center">
                            <Trash weight="bold" size={12}/>
                          </button>
                        </div>
                        <input value={m.description || ""} onChange={e => updMT(i, "description", e.target.value)} placeholder="Description (shown to visitors)"
                          className="mt-1 w-full h-8 px-2 border border-slate-200 text-xs bg-white"/>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Working Hours</label>
                  <div className="mt-1 space-y-1">
                    {DAY_LABELS.map((lbl, d) => (
                      <div key={d} className="flex items-center gap-2">
                        <div className="w-10 text-xs font-black text-slate-700">{lbl}</div>
                        <div className="flex-1 flex flex-wrap gap-1 items-center">
                          {(current.working_hours[String(d)] || []).map((rg, i) => (
                            <div key={i} className="flex items-center gap-1">
                              <input type="time" value={rg.start} onChange={e => updHours(String(d), i, "start", e.target.value)}
                                className="h-8 w-24 border border-slate-200 text-xs bg-white px-1"/>
                              <span className="text-slate-400 text-xs">–</span>
                              <input type="time" value={rg.end} onChange={e => updHours(String(d), i, "end", e.target.value)}
                                className="h-8 w-24 border border-slate-200 text-xs bg-white px-1"/>
                              <button onClick={() => rmHour(String(d), i)} className="text-slate-400 hover:text-red-500 text-xs">✕</button>
                            </div>
                          ))}
                          <button onClick={() => addHour(String(d))} className="text-[10px] font-bold text-slate-800 hover:text-black">+ range</button>
                          {(current.working_hours[String(d)] || []).length === 0 && (
                            <span className="text-[10px] text-slate-400 italic">off</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button onClick={save} disabled={busy}
                    className="h-10 px-4 bg-slate-900 hover:bg-black text-white text-xs font-black uppercase tracking-widest disabled:opacity-50"
                    data-testid="booking-save">
                    {busy ? "Saving…" : "Save Profile"}
                  </button>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={current.active} onChange={e => updField("active")(e.target.checked)}/>
                    Active (public link works)
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* First Available on the Team — scheduler mode */}
        <FirstAvailablePanel profiles={profiles} onBooked={load}/>

        {/* Upcoming bookings */}
        <div className="rounded-2xl bg-white border-2 border-slate-200 overflow-hidden">
          <div className="px-3 py-2 bg-slate-100 flex items-center justify-between">
            <div className="text-xs font-black uppercase tracking-widest text-slate-600">Upcoming Bookings · {bookings.length}</div>
          </div>
          {bookings.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">No upcoming bookings.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {bookings.map(b => (
                <div key={b.id} className="px-3 py-2 grid grid-cols-12 gap-2 text-sm items-center hover:bg-slate-50">
                  <div className="col-span-3 font-bold text-slate-900">{new Date(b.starts_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                  <div className="col-span-3">
                    <div className="font-bold text-slate-800">{b.meeting_type_name}</div>
                    <div className="text-[10px] text-slate-500">with {b.profile_display_name}</div>
                  </div>
                  <div className="col-span-4">
                    <div className="text-slate-800 truncate">{b.invitee_name} · <span className="text-slate-500 text-xs">{b.invitee_email}</span></div>
                    {b.property_address && <div className="text-[10px] text-slate-500 flex items-center gap-1"><House size={10}/>{b.property_address}</div>}
                  </div>
                  <div className="col-span-2 text-right">
                    <button onClick={() => cancelBooking(b.id)} className="text-[10px] text-red-600 hover:text-red-800 font-bold">Cancel</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// FirstAvailablePanel — scheduler picks a meeting type + window, sees the
// union of open slots across the team, and books the least-loaded tech in
// one click. Ideal for jobs where any qualified tech will do.
// ═══════════════════════════════════════════════════════════════════════
function FirstAvailablePanel({ profiles, onBooked }) {
  // Build the union of meeting type slugs across every active profile.
  const teamTypes = (() => {
    const seen = new Map();
    for (const p of profiles) {
      if (!p.active) continue;
      for (const mt of p.meeting_types || []) {
        if (!seen.has(mt.slug)) seen.set(mt.slug, mt);
      }
    }
    return Array.from(seen.values());
  })();

  const [typeSlug, setTypeSlug] = useState(teamTypes[0]?.slug || "");
  const [days, setDays] = useState(7);
  const [slots, setSlots] = useState([]);
  const [teamSize, setTeamSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [form, setForm] = useState({
    invitee_name: "", invitee_email: "", invitee_phone: "",
    property_address: "", notes: "",
  });
  const [booking, setBooking] = useState(false);
  const [lastBooked, setLastBooked] = useState(null);

  useEffect(() => {
    if (!typeSlug && teamTypes.length) setTypeSlug(teamTypes[0].slug);
  }, [teamTypes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSlots = async () => {
    if (!typeSlug) return;
    setLoading(true); setSlots([]); setPickedSlot(null);
    try {
      const r = await api.get(`/booking/team-slots?type=${encodeURIComponent(typeSlug)}&days=${days}`);
      setSlots(r.data.slots || []);
      setTeamSize(r.data.team_size || 0);
      if ((r.data.slots || []).length === 0) {
        toast.info(r.data.note || "No open slots in that window");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Load failed")); }
    finally { setLoading(false); }
  };

  const book = async () => {
    if (!pickedSlot) { toast.error("Pick a slot first"); return; }
    if (!form.invitee_name.trim() || !form.invitee_email.trim()) {
      toast.error("Add invitee name + email"); return;
    }
    setBooking(true);
    try {
      const r = await api.post("/booking/team-reserve", {
        meeting_type_slug: typeSlug,
        starts_at: pickedSlot.starts_at,
        ...form,
      });
      toast.success(`Booked with ${r.data.assigned_to}`);
      setLastBooked(r.data);
      setPickedSlot(null);
      setForm({ invitee_name: "", invitee_email: "", invitee_phone: "", property_address: "", notes: "" });
      await loadSlots();
      onBooked?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Booking failed")); }
    finally { setBooking(false); }
  };

  // Group slots by date for readable display.
  const slotsByDate = slots.reduce((acc, s) => {
    const d = s.starts_at.slice(0, 10);
    (acc[d] = acc[d] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="rounded-2xl bg-gradient-to-br from-cyan-50 to-white border-2 border-cyan-200 overflow-hidden" data-testid="first-available-panel">
      <div className="px-4 py-3 bg-cyan-600 text-white flex items-center gap-2">
        <Lightning weight="fill" size={18}/>
        <div className="font-black text-sm uppercase tracking-widest">First Available on the Team</div>
        <div className="text-xs opacity-80 ml-2 hidden sm:block">Scheduler mode — auto-assigns the least-loaded tech</div>
      </div>
      <div className="p-4 space-y-3">
        {teamTypes.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 font-medium" data-testid="first-available-empty">
            No booking profiles configured yet. Enable at least one teammate above.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex-1 min-w-[160px]">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Meeting type</div>
                <select value={typeSlug} onChange={e => { setTypeSlug(e.target.value); setSlots([]); setPickedSlot(null); }}
                  data-testid="first-available-type"
                  className="w-full h-10 px-3 border-2 border-slate-200 rounded-lg font-medium">
                  {teamTypes.map(t => <option key={t.slug} value={t.slug}>{t.name} · {t.duration_min}min</option>)}
                </select>
              </label>
              <label className="w-24">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Window</div>
                <select value={days} onChange={e => setDays(Number(e.target.value))}
                  data-testid="first-available-days"
                  className="w-full h-10 px-3 border-2 border-slate-200 rounded-lg font-medium">
                  <option value="3">3 days</option>
                  <option value="7">7 days</option>
                  <option value="14">14 days</option>
                  <option value="30">30 days</option>
                </select>
              </label>
              <button type="button" onClick={loadSlots} disabled={loading || !typeSlug}
                data-testid="first-available-load"
                className="h-10 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-60">
                <Sparkle weight="fill" size={12}/> {loading ? "Loading…" : "Find slots"}
              </button>
              {teamSize > 0 && (
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 inline-flex items-center gap-1">
                  <Users weight="fill" size={12}/> {teamSize} on team
                </div>
              )}
            </div>

            {slots.length > 0 && (
              <div className="border-2 border-slate-200 rounded-xl overflow-hidden bg-white" data-testid="first-available-slots">
                {Object.entries(slotsByDate).slice(0, 5).map(([d, day_slots]) => (
                  <div key={d} className="border-b border-slate-100 last:border-b-0">
                    <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-600">
                      {new Date(d + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                    </div>
                    <div className="p-2 flex flex-wrap gap-1.5">
                      {day_slots.slice(0, 24).map(s => {
                        const active = pickedSlot?.starts_at === s.starts_at;
                        return (
                          <button key={s.starts_at} type="button" onClick={() => setPickedSlot(s)}
                            data-testid={`first-available-slot-${s.starts_at}`}
                            className={`h-8 px-2 rounded-md text-[11px] font-black inline-flex items-center gap-1 border-2 transition ${
                              active ? "bg-cyan-600 border-cyan-600 text-white"
                              : "bg-white border-slate-200 hover:border-cyan-500 text-slate-700"}`}>
                            {new Date(s.starts_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                            <span className={`text-[9px] font-black uppercase tracking-widest px-1 rounded ${
                              active ? "bg-white text-cyan-700" : "bg-slate-100 text-slate-600"}`}>
                              {s.free_count} free
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {pickedSlot && (
              <div className="rounded-xl bg-white border-2 border-cyan-500 p-3 space-y-3" data-testid="first-available-form">
                <div className="text-xs font-black uppercase tracking-widest text-cyan-700">
                  Booking {new Date(pickedSlot.starts_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </div>
                <div className="text-[10px] text-slate-500">
                  Field Radar will auto-assign the least-loaded of: {pickedSlot.available_user_names.join(", ")}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input type="text" placeholder="Invitee name*" value={form.invitee_name}
                    onChange={e => setForm({ ...form, invitee_name: e.target.value })}
                    data-testid="first-available-name"
                    className="h-10 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
                  <input type="email" placeholder="Invitee email*" value={form.invitee_email}
                    onChange={e => setForm({ ...form, invitee_email: e.target.value })}
                    data-testid="first-available-email"
                    className="h-10 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
                  <input type="tel" placeholder="Invitee phone" value={form.invitee_phone}
                    onChange={e => setForm({ ...form, invitee_phone: e.target.value })}
                    data-testid="first-available-phone"
                    className="h-10 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
                  <input list="known-addresses" autoComplete="off" type="text" placeholder="Property address" value={form.property_address}
                    onChange={e => setForm({ ...form, property_address: e.target.value })}
                    data-testid="first-available-address"
                    className="h-10 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
                </div>
                <textarea placeholder="Notes for the tech (optional)" value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  data-testid="first-available-notes"
                  className="w-full h-16 px-3 py-2 border-2 border-slate-200 rounded-lg font-medium text-sm"/>
                <button type="button" onClick={book} disabled={booking}
                  data-testid="first-available-book"
                  className="w-full h-11 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  <PaperPlaneTilt weight="fill" size={14}/> {booking ? "Booking…" : "Auto-assign & book"}
                </button>
              </div>
            )}

            {lastBooked && !pickedSlot && (
              <div className="rounded-lg bg-emerald-50 border-2 border-emerald-200 p-3" data-testid="first-available-success">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-1">Booked ✓</div>
                <div className="text-sm text-emerald-900">
                  <span className="font-black">{lastBooked.assigned_to}</span> got the booking —{" "}
                  {new Date(lastBooked.starts_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}.
                  Considered {lastBooked.considered_count} teammate{lastBooked.considered_count === 1 ? "" : "s"}.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
